/**
 * A system and an integration as peeks (opened from Now's map lanes and feed): a compact summary of the last 24 hours
 * and the way on — "Open system" / "Open integration" go to their pages (docs/systems.md "Systems view"). An
 * integration of a system's peek opens as a second peek; a run of an integration's peek too.
 */
import { useMemo } from 'react';
import dayjs from 'dayjs';
import { Api } from '../lib/api.ts';
import { useAuth } from '../lib/auth.tsx';
import { useEnv } from '../lib/envs.tsx';
import { useAsync } from '../lib/hooks.ts';
import { count, duration } from '../lib/format.ts';
import { cadenceLabel, integrationsOf } from '../lib/integrations.ts';
import { integrationPath, integrationQuery, percent, systemPath, systemQuery } from '../lib/systemsview.ts';
import { stripSystem, systemInfo, systemColor, systemIcon, systemLogo, systemName } from '../lib/systems.ts';
import { KIND_LABEL } from '../lib/systemedit.ts';
import { SystemMark } from '../components/SystemEditor.tsx';
import { cx, noun } from '../design/primitives.tsx';
import { ActionLink, IntegrationRow, Pair, RunBars, StatusMark } from '../systems/parts.tsx';
import { KickerRow } from './RunPeek.tsx';

const DAY = new URLSearchParams({ from: 'now-24h' });
const search = (q: string) => `/s?${new URLSearchParams({ q })}`;

function Loading() {
  return (
    <div className="flex flex-col gap-4 pt-2" aria-busy="true">
      <span className="mv-shimmer rounded-chip h-7 w-2/3" />
      <span className="mv-shimmer rounded-chip h-4 w-1/2" />
      <span className="mv-shimmer rounded-card h-24 w-full" />
      <span className="mv-shimmer rounded-card h-40 w-full" />
    </div>
  );
}

export function SystemPeek({ code, onIntegration }: { code: string; onIntegration: (process: string) => void }) {
  const { tenant } = useAuth();
  const { link } = useEnv();
  const tid = tenant?.id ?? '';
  const res = useAsync(() => Api.systemOverview(tid, code, DAY), [tid, code]);
  const info = systemInfo(code);
  const now = Date.now();
  const o = res.data;
  const split = useMemo(() => (o ? integrationsOf(o.integrations, o.code) : null), [o]);
  const failing = o?.integrations.filter((i) => i.status === 'failing').length ?? 0;
  const overdue = o?.integrations.filter((i) => i.status === 'quiet').length ?? 0;
  const ordered = split ? [...split.in.map((i) => ({ i, dir: 'in' as const })), ...split.out.map((i) => ({ i, dir: 'out' as const })), ...split.within.map((i) => ({ i, dir: 'within' as const }))]
    .sort((a, b) => (a.i.status === 'failing' ? 0 : a.i.status === 'quiet' ? 1 : 2) - (b.i.status === 'failing' ? 0 : b.i.status === 'quiet' ? 1 : 2) || b.i.runs - a.i.runs) : [];
  const query = systemQuery(code, info?.aliases ?? [], o?.dataModels.map((d) => d.cls) ?? []);

  return (
    <article className="flex flex-col gap-6 pt-2" data-peek-system={code}>
      <header className="flex items-center gap-3 min-w-0">
        <SystemMark code={code} color={systemColor(code)} icon={systemIcon(code)} logo={systemLogo(code)} size={44} />
        <div className="min-w-0">
          <h2 className="m-0 text-title font-semibold tracking-tight truncate">{systemName(code)}</h2>
          <p className="m-0 text-small text-n8 truncate"><span className="font-mono">{systemName(code) === code ? 'System' : code}</span>{info?.kind && <> · {KIND_LABEL[info.kind] ?? info.kind}</>}{info?.vendor && <> · {info.vendor}</>}</p>
        </div>
      </header>
      {info?.description && <p className="m-0 -mt-2 text-small text-n9 leading-relaxed line-clamp-3">{info.description}</p>}
      <div className="flex items-center gap-2 flex-wrap">
        <ActionLink to={link(systemPath(code))}>Open system</ActionLink>
        <ActionLink to={link(search(query))} quiet>Search its data</ActionLink>
      </div>
      {res.error && !o && <p className="m-0 text-small text-fail">{res.error}</p>}
      {!o && !res.error && <Loading />}
      {o && (
        <>
          <p className="m-0 text-body text-n9">
            {count(o.integrations.length)} {o.integrations.length === 1 ? 'integration' : 'integrations'}
            {failing > 0 && <span className="text-fail"> · {count(failing)} failing</span>}
            {overdue > 0 && <span className="text-warn"> · {count(overdue)} overdue</span>}
            {failing + overdue === 0 && o.integrations.length > 0 && <span className="text-n8"> · all running</span>}
          </p>
          {ordered.length > 0 && (
            <section>
              <KickerRow title="Integrations" fact="last 24 h" />
              <div className="sys-rows sys-narrow -mx-2.5">
                {ordered.slice(0, 8).map(({ i, dir }) => <IntegrationRow key={`${i.process}|${i.from}|${i.to}`} i={i} now={now} other={{ dir }} onOpen={onIntegration} />)}
              </div>
              {ordered.length > 8 && <p className="m-0 mt-1 text-micro text-n7">{count(ordered.length - 8)} more on the system’s page</p>}
            </section>
          )}
          {o.dataModels.length > 0 && (
            <section>
              <KickerRow title="Data models" fact={`${count(o.dataModels.length)}`} />
              <div className="flex flex-wrap gap-1.5">
                {o.dataModels.slice(0, 10).map((d) => (
                  <span key={d.cls} title={d.cls} className="inline-flex items-center gap-1.5 h-7 px-2.5 rounded-chip bg-n3 text-small text-n9">
                    {noun(d.cls, 2)}<span className="text-micro text-n7 tabular-nums">{count(d.documents)}</span>
                    {d.failed > 0 && <i className="size-1.5 rounded-full bg-fail" aria-label={`${d.failed} failed`} />}
                  </span>
                ))}
              </div>
            </section>
          )}
          {o.partial && <p className="m-0 text-micro text-warn">Some vaults did not answer; this may be incomplete.</p>}
        </>
      )}
    </article>
  );
}

export function IntegrationPeek({ process, onRun }: { process: string; onRun: (executionId: string) => void }) {
  const { tenant } = useAuth();
  const { link } = useEnv();
  const tid = tenant?.id ?? '';
  const res = useAsync(() => Api.integration(tid, process, DAY), [tid, process]);
  const d = res.data;
  const now = Date.now();
  const cadence = d ? cadenceLabel(d.cadenceMs) : null;
  const failedRuns = d?.latest.filter((r) => r.failed > 0).slice(0, 4) ?? [];

  return (
    <article className="flex flex-col gap-6 pt-2" data-peek-integration={process}>
      <header className="flex flex-col gap-2 min-w-0">
        <h2 className="m-0 text-title font-semibold tracking-tight [overflow-wrap:anywhere]">{stripSystem(process)}</h2>
        <div className="flex items-center gap-3 flex-wrap text-small">
          {d && d.integrations.map((i) => <Pair key={`${i.from}>${i.to}`} from={i.from} to={i.to} size={24} names />)}
          {d && <StatusMark status={d.status} />}
        </div>
        <p className="m-0 font-mono text-micro text-n7 truncate">{process}</p>
      </header>
      <div className="flex items-center gap-2 flex-wrap">
        <ActionLink to={link(integrationPath(process))}>Open integration</ActionLink>
        <ActionLink to={link(search(integrationQuery(process)))} quiet>Search its data</ActionLink>
      </div>
      {res.error && !d && <p className="m-0 text-small text-fail">{res.error}</p>}
      {!d && !res.error && <Loading />}
      {d && (
        <>
          <section>
            <KickerRow title="Runs" fact="last 24 h" />
            <RunBars bars={d.timeline} height={44} compact />
            <p className="m-0 mt-3 text-small text-n9 leading-relaxed">
              {count(d.runs)} {d.runs === 1 ? 'run' : 'runs'}
              {d.runs > 0 && <> · <span className={cx(d.failedRuns > 0 && 'text-fail')}>{percent(d.successRate)} without failures</span></>}
              {cadence && <> · runs {cadence}</>}
              {d.lastRun && <> · last {dayjs(d.lastRun).format('HH:mm')}</>}
            </p>
            {d.status === 'quiet' && d.lastRun && <p className="m-0 mt-1 text-small text-warn">Silent for {duration(Math.max(60_000, now - d.lastRun))}{d.cadenceMs ? `, usually ${cadence ?? `every ${duration(d.cadenceMs)}`}` : ''}</p>}
          </section>
          {d.dataModels.length > 0 && (
            <section>
              <KickerRow title="Carries" />
              <div className="flex flex-wrap gap-1.5">
                {d.dataModels.map((c) => <span key={c} title={c} className="inline-flex items-center h-7 px-2.5 rounded-chip bg-n3 text-small text-n9">{noun(c, 2)}</span>)}
              </div>
            </section>
          )}
          {failedRuns.length > 0 && (
            <section>
              <KickerRow title="Failed runs" fact={`${count(d.failedRuns)} in 24 h`} />
              <div className="flex flex-col -mx-2.5">
                {failedRuns.map((r) => (
                  <button key={r.executionId} type="button" onClick={() => onRun(r.executionId)}
                    className="grid grid-cols-[48px_minmax(0,1fr)_auto] gap-3 items-center px-2.5 py-2 rounded-chip bg-transparent border-0 cursor-pointer text-left text-small hover:bg-n3">
                    <span className="font-mono text-micro text-n7">{dayjs(r.first).format('HH:mm')}</span>
                    <span className="truncate text-n9">{r.classes.map((c) => noun(c)).join(', ') || 'run'}</span>
                    <span className="text-fail whitespace-nowrap">{count(r.failed)} failed</span>
                  </button>
                ))}
              </div>
            </section>
          )}
          {d.partial && <p className="m-0 text-micro text-warn">Some vaults did not answer; this may be incomplete.</p>}
        </>
      )}
    </article>
  );
}
